// File Metadata:
// Filename: EditShipmentlogForm.js
// Version: 1.00
// Created: 2022-10-24 18:49:28
// Last Modified: 2025-04-01 16:55:19
// Authors: [Your Name]
// Description: A brief summary of what this file does

import { useState, useEffect } from 'react'
import { 
    Box, Paper, Typography, Grid, TextField, Button,
    FormControl, InputLabel, Select, MenuItem,
    Stack, Divider, ImageList, ImageListItem,
    Card, CardHeader, CardContent, IconButton,
    Alert, Chip, Avatar, Dialog, DialogContent, DialogTitle, IconButton as MuiIconButton, ImageListItemBar, CircularProgress
} from '@mui/material'
import { 
    Save as SaveIcon,
    Delete as DeleteIcon,
    PhotoCamera as CameraIcon,
    Close as CloseIcon,
    DownloadForOffline as DownloadIcon
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import useAuth from '../../hooks/useAuth'
import { TYPES } from '../../config/constants'
import { format } from 'date-fns'
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, 
         TimelineContent, TimelineDot, TimelineOppositeContent } from '@mui/lab'
import { Autocomplete } from '@mui/material'
import Camera from '../../components/Camera'
import { nanoid } from '@reduxjs/toolkit'

import { 
    useUpdateShipmentlogMutation,
    useUpdateShipmentStatusMutation,
    useAddShipmentCommentMutation,
	useDeleteShipmentlogMutation,
    useAddShipmentImageMutation,
    useDeleteShipmentImageMutation
} from '../api/shipmentlogsApiSlice'
import { SHIPMENT_STATUS, PRIORITY_LEVELS } from '../../config/shipmentConstants'
import uploadToImageKit from '../../functions/imagekit'
import ShipmentTimeline from './components/ShipmentTimeline'
import { downloadAllShipmentImages } from '../../functions/downloadUtils'

const EditShipmentlogForm = ({ shipmentlog, users }) => {
	const { isManager, isAdmin } = useAuth()
	const [updateShipmentlog, { isLoading, isSuccess, isError, error }] = useUpdateShipmentlogMutation()
	const [deleteShipmentlog, { isSuccess: isDelSuccess, isError: isDelError, error: delerror }] =
		useDeleteShipmentlogMutation()
	const [updateStatus] = useUpdateShipmentStatusMutation()
    const [addComment] = useAddShipmentCommentMutation()
    const [addShipmentImage] = useAddShipmentImageMutation()
    const [deleteShipmentImage] = useDeleteShipmentImageMutation()
    const [comment, setComment] = useState('')
    const { userId } = useAuth()

	const navigate = useNavigate()

	const [sscc, setSSCC] = useState(shipmentlog.sscc)
	const [type, setType] = useState(shipmentlog.type)
	const [tour, setTour] = useState(shipmentlog.tour)
	const [img, setImg] = useState(shipmentlog.img)
	const [note, setNote] = useState(shipmentlog.note)
    const [selectedUser, setSelectedUser] = useState(shipmentlog.assignedTo || null)
    const [cameraOpen, setCameraOpen] = useState(false)
	const [progress, setProgress] = useState({})
    const [uploadQueue, setUploadQueue] = useState([])
    const [isUploading, setIsUploading] = useState(false)
    const [previewImage, setPreviewImage] = useState(null)

	useEffect(() => {}, [isSuccess, isDelSuccess, navigate])

    useEffect(() => {
        const processUploadQueue = async () => {
            if (uploadQueue.length > 0 && !isUploading) {
                setIsUploading(true)
                const nextUpload = uploadQueue[0]

                try {
                    // Upload to ImageKit
                    const uploadedImage = await uploadToImageKit(nextUpload.data, (progress) => {
                        setProgress(prev => ({
                            ...prev,
                            [nextUpload.tempId]: progress
                        }))
                    })

                    // Save to database
                    await addShipmentImage({
                        id: shipmentlog.id,
                        imageData: uploadedImage
                    }).unwrap()

                    // Update local state
                    setImg(prevImg => prevImg.map(img => 
                        img.fileId === nextUpload.tempId ? uploadedImage : img
                    ))
                } catch (err) {
                    console.error('Failed to process image:', err)
                    setImg(prevImg => prevImg.filter(img => img.fileId !== nextUpload.tempId))
                }

                // Remove processed item from queue
                setUploadQueue(prev => prev.slice(1))
                setIsUploading(false)
            }
        }

        processUploadQueue()
    }, [uploadQueue, isUploading, shipmentlog.id, addShipmentImage])

	const onSSCCChanged = e => setSSCC(e.target.value)
	const onTypeChanged = e => setType(e.target.value)
	const onTourChanged = e => setTour(e.target.value)
	const onImgChanged = e => setImg(e.target.value)
	const onNoteChanged = e => setNote(e.target.value)

	const canSave = [sscc, type, tour, img].every(Boolean) && !isLoading

	const onSaveShipmentlogClicked = async e => {
		if (canSave) {
            // Merge existing and new images while removing any that were deleted
            const currentImageIds = img.map(image => image.fileId);
            const updatedImageData = [
                // Keep original images that weren't deleted
                ...shipmentlog.img.filter(image => currentImageIds.includes(image.fileId)),
                // Add new images
                ...img.filter(image => !shipmentlog.img.find(orig => orig.fileId === image.fileId))
            ];

            await updateShipmentlog({ 
                id: shipmentlog.id, 
                sscc, 
                type, 
                tour, 
                img: updatedImageData, 
                note 
            });
		}
	}

	const onDeleteShipmentlogClicked = async () => {
		await deleteShipmentlog({ id: shipmentlog.id })
	}

	const onAddPictureClicked = () => {
        setCameraOpen(true)
    }

    const handlePhotoCapture = async (dataUri) => {
        const tempPhoto = {
            fileId: nanoid(),
            fileName: `shipment_${shipmentlog.id}_${nanoid()}.jpg`,
            url: dataUri,
            isUploading: true
        }

        // Add to display immediately
        setImg(prevImg => [...prevImg, tempPhoto])
        setCameraOpen(false)

        // Add to upload queue
        setUploadQueue(prev => [...prev, {
            tempId: tempPhoto.fileId,
            data: {
                img: dataUri,
                name: tempPhoto.fileName,
                tags: [`shipment_${shipmentlog.id}`]
            }
        }])
    }

    const handleRemovePhoto = async (fileId) => {
        try {
            // Update local state immediately for optimistic UI update
            setImg(prevImg => prevImg.filter(photo => photo.fileId !== fileId))
            
            // Call the delete mutation
            await deleteShipmentImage({
                id: shipmentlog.id,
                fileId
            }).unwrap()
            
        } catch (err) {
            console.error('Failed to delete image:', err)
            // Restore the image in state if deletion failed
            setImg(prevImg => [...prevImg, shipmentlog.img.find(img => img.fileId === fileId)])
        }
    }

	const handleStatusChange = async (newStatus) => {
        try {
            await updateStatus({
                id: shipmentlog.id,
                status: newStatus,
                note: `Status changed to ${newStatus}`
            }).unwrap()
        } catch (err) {
            console.error('Failed to update status:', err)
        }
    }

    const handlePriorityChange = async (newPriority) => {
        try {
            await updateStatus({
                id: shipmentlog.id,
                priority: newPriority,
                note: `Priority changed to ${newPriority}`
            }).unwrap()
        } catch (err) {
            console.error('Failed to update priority:', err)
        }
    }

    const handleAddComment = async () => {
        if (!comment.trim()) return
        
        try {
            await addComment({
                id: shipmentlog.id,
                text: comment,
                author: userId
            }).unwrap()
            setComment('')
        } catch (err) {
            console.error('Failed to add comment:', err)
        }
    }

    const handleAssign = async (newUser) => {
        try {
            const result = await updateShipmentlog({
                id: shipmentlog.id,
                assignedTo: newUser?.id || null  // Send the user ID or null for unassign
            }).unwrap()

            // Don't navigate, just update local state
            setSelectedUser(newUser)
        } catch (err) {
            console.error('Failed to assign shipment:', err)
        }
    }

	const created = new Date(shipmentlog.createdAt).toLocaleString('fr-BE', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		hour: 'numeric',
		minute: 'numeric',
		second: 'numeric'
	})
	const updated = new Date(shipmentlog.updatedAt).toLocaleString('fr-BE', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		hour: 'numeric',
		minute: 'numeric',
		second: 'numeric'
	})

	const errClass = isError || isDelError ? 'errmsg' : 'offscreen'

	const errContent = (error?.data?.message || delerror?.data?.message) ?? ''

    const handleImageClick = (imageUrl) => {
        setPreviewImage(imageUrl)
    }

    const handleDownloadAll = async () => {
        if (img?.length) {
            await downloadAllShipmentImages(img);
        }
    };

    const renderImageList = () => (
        <ImageList sx={{ maxWidth: '100%' }} cols={2} gap={8}>
            {img.map(data => (
                <ImageListItem 
                    key={data.fileId} 
                    onClick={() => handleImageClick(data.url)}
                    sx={{ cursor: 'pointer' }}
                >
                    <img
                        src={data.url}
                        alt={data.fileId}
                        loading="lazy"
                        style={{ 
                            borderRadius: 1,
                            opacity: data.isUploading ? 0.5 : 1 
                        }}
                    />
                    <ImageListItemBar
                        position="top"
                        actionIcon={
                            data.isUploading ? (
                                <CircularProgress 
                                    size={24}
                                    variant="determinate"
                                    value={progress[data.fileId] || 0}
                                    sx={{ m: 1, color: 'white' }}
                                />
                            ) : (
                                <IconButton
                                    sx={{ color: 'white' }}
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent image preview
                                        handleRemovePhoto(data.fileId);
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            )
                        }
                    />
                </ImageListItem>
            ))}
        </ImageList>
    )

    const imagePreviewDialog = (
        <Dialog
            open={Boolean(previewImage)}
            onClose={() => setPreviewImage(null)}
            maxWidth="lg"
            fullWidth
        >
            <DialogContent sx={{ p: 0 }}>
                {previewImage && (
                    <img
                        src={previewImage}
                        alt="Preview"
                        style={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: '90vh',
                            objectFit: 'contain'
                        }}
                    />
                )}
            </DialogContent>
        </Dialog>
    )

	const content = (
        <Grid container spacing={2}>
            {/* Left Column - Form Fields */}
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                    <CardHeader 
                        title={`Edit Shipment #${shipmentlog.shipmentlog}`}
                        action={
                            <Stack direction="row" spacing={1}>
                                <IconButton
                                    onClick={onSaveShipmentlogClicked}
                                    disabled={!canSave}
                                    color="primary"
                                >
                                    <SaveIcon />
                                </IconButton>
                                {(isManager || isAdmin) && (
                                    <IconButton
                                        onClick={onDeleteShipmentlogClicked}
                                        color="error"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </Stack>
                        }
                    />
                    <CardContent>
                        {(isError || isDelError) && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error?.data?.message || delerror?.data?.message}
                            </Alert>
                        )}

                        <Stack spacing={3}>
                            <TextField
                                label="SSCC"
                                value={sscc}
                                onChange={onSSCCChanged}
                                fullWidth
                                helperText="3-20 letters"
                            />

                            <FormControl fullWidth>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    value={type}
                                    label="Type"
                                    onChange={onTypeChanged}
                                >
                                    {TYPES.tasklog.map(type => (
                                        <MenuItem key={type.taskId} value={type.taskId}>
                                            {type.text}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                label="Tour"
                                value={tour}
                                onChange={onTourChanged}
                                fullWidth
                                helperText="3-20 letters"
                            />

                            <TextField
                                label="Notes"
                                multiline
                                rows={4}
                                value={note}
                                onChange={onNoteChanged}
                                fullWidth
                            />

                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Images
                                </Typography>
                                {renderImageList()}
                                {(isManager || isAdmin) && (
                                    <>
                                        <Button
                                            startIcon={<CameraIcon />}
                                            onClick={onAddPictureClicked}
                                            variant="outlined"
                                            sx={{ mt: 1 }}
                                        >
                                            Add Photo
                                        </Button>
                                        <Button
                                            startIcon={<DownloadIcon />}
                                            onClick={handleDownloadAll}
                                            variant="outlined"
                                            sx={{ mt: 1}}
                                        >
                                            Download All Photos
                                        </Button>
                                    </>
                                )}
                            </Box>

                            <Divider />
                            
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="caption" color="text.secondary">
                                    Created: {format(new Date(shipmentlog.createdAt), 'PPpp')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Updated: {format(new Date(shipmentlog.updatedAt), 'PPpp')}
                                </Typography>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Paper>
            </Grid>

            {/* Right Column - Status and New Sections */}
            <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                    {/* Assignment Section - New */}
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>Assignment</Typography>
                        <Stack spacing={2}>
                            {shipmentlog.assignedTo && (
                                <Chip
                                    avatar={<Avatar>{shipmentlog.assignedTo.username?.[0]}</Avatar>}
                                    label={`Assigned to: ${shipmentlog.assignedTo.username}`}
                                    onDelete={() => handleAssign(null)}
                                />
                            )}
                            <Autocomplete
                                options={users || []}
                                getOptionLabel={(option) => option.username}
                                renderInput={(params) => (
                                    <TextField {...params} label="Assign to" />
                                )}
                                value={selectedUser}
                                onChange={(_, newValue) => handleAssign(newValue)}
                                isOptionEqualToValue={(option, value) => option.id === value?.id}
                            />
                        </Stack>
                    </Paper>

                    {/* Timeline Section - New */}
					<ShipmentTimeline statusHistory = {shipmentlog.statusHistory} />
                    
                    {/* Status Section */}
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Status Management
                        </Typography>
                        <Stack spacing={2}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={shipmentlog.status}
                                    label="Status"
                                    onChange={(e) => handleStatusChange(e.target.value)}
                                >
                                    {Object.values(SHIPMENT_STATUS).map(status => (
                                        <MenuItem key={status.value} value={status.value}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip 
                                                    label={status.label}
                                                    size="small"
                                                    color={status.color}
                                                />
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel>Priority</InputLabel>
                                <Select
                                    value={shipmentlog.priority}
                                    label="Priority"
                                    onChange={(e) => handlePriorityChange(e.target.value)}
                                >
                                    {Object.values(PRIORITY_LEVELS).map(priority => (
                                        <MenuItem key={priority.value} value={priority.value}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip 
                                                    label={priority.label}
                                                    size="small"
                                                    color={priority.color}
                                                />
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                    </Paper>

                    {/* Comments Section */}
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Comments
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Add a comment..."
                            />
                            <Button 
                                variant="contained"
                                onClick={handleAddComment}
                                disabled={!comment.trim()}
                            >
                                Add Comment
                            </Button>
                            <Divider />
                            <Stack spacing={1}>
                                {shipmentlog.comments?.map((comment, index) => (
                                    <Paper key={index} elevation={0} sx={{ p: 1, bgcolor: 'grey.100' }}>
                                        <Typography variant="body2">{comment.text}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(comment.timestamp).toLocaleString()}
                                        </Typography>
                                    </Paper>
                                ))}
                            </Stack>
                        </Stack>
                    </Paper>

                    {/* Status History */}
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Status History
                        </Typography>
                        <Stack spacing={1}>
                            {shipmentlog.statusHistory?.map((status, index) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                        label={status.status}
                                        color={SHIPMENT_STATUS[status.status]?.color || 'default'}
                                        size="small"
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {new Date(status.timestamp).toLocaleString()}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Paper>
                </Stack>
            </Grid>
        </Grid>
    )

    const cameraPanelContent = (
        <Dialog
            open={cameraOpen}
            onClose={() => setCameraOpen(false)}
            maxWidth="md"
            fullWidth
        >
            <DialogContent sx={{ p: 0, position: 'relative' }}>
                <Camera 
                    onTakePhoto={handlePhotoCapture}
                    isImageMirror={false}
                    idealFacingMode="environment"
                />
                <MuiIconButton
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        zIndex: 2,
                        color: 'white'
                    }}
                    onClick={() => setCameraOpen(false)}
                >
                    <CloseIcon />
                </MuiIconButton>
            </DialogContent>
        </Dialog>
    )

	return (
        <>
            {content}
            {imagePreviewDialog}
            {cameraPanelContent}
        </>
    )
}

export default EditShipmentlogForm
